import {
  Button,
  ButtonTypes,
  FormTextField,
  IAddress,
  IModalProps,
  IProfile,
  Modal,
  countries,
  getPrimaryAddressFromProfile,
  getStatesByCountry,
} from '@comptia-sso/core';
import { FormikProvider, useFormik } from 'formik';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Styles.
import styles from './AddressVerificationModal.module.scss';

// Validations.
import { getValidationSchema } from './validationSchema';

interface IAddressVerificationModalProps extends IModalProps {
  profile?: IProfile;
  onSubmit: (values: Partial<IAddress>) => void;
}

export const AddressVerificationModal = (
  props: IAddressVerificationModalProps,
): ReactElement => {
  const { onSubmit, profile } = props;
  const { t } = useTranslation();
  const primaryAddress = useMemo(
    () => getPrimaryAddressFromProfile(profile),
    [profile],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      address1: primaryAddress?.address1 || '',
      address2: primaryAddress?.address2 || '',
      address3: primaryAddress?.address3 || '',
      city: primaryAddress?.city || '',
      country: primaryAddress?.country || 'USA',
      givenName: {
        firstName: profile?.givenName.firstName || '',
        lastName: profile?.givenName.lastName || '',
        middleName: profile?.givenName.middleName || '',
        suffix: profile?.givenName.suffix || '',
      },
      isPrimary: primaryAddress?.isPrimary ?? true,
      postalCode: primaryAddress?.postalCode || '',
      state: primaryAddress?.state || '',
    },
    onSubmit,
    validateOnChange: false,
    validationSchema: getValidationSchema(t),
  });

  const states = useMemo(
    () => getStatesByCountry(formik?.values.country || 'USA'),
    [formik?.values.country],
  );

  return (
    <Modal {...props} title={t('AddressVerificationModal.Title')}>
      <FormikProvider value={formik}>
        <form className={styles.form} noValidate onSubmit={formik.handleSubmit}>
          <div className={styles.grid}>
            <p>
              <small
                dangerouslySetInnerHTML={{
                  __html: t('EditProfile.LegalName'),
                }}
              />
            </p>
            {/* 1/12/25 - Limit is now 25 characters until we update 365 CRM to allow
                greater than 25 characters for names. Change was due for store as order sync was failing.
                If they have name longer then 25 characters leave as the old max values as not to cause
                issue for legacy users or existing functionality.
                Plan is to increase CRM and restore original max or decide new max rules */}
            <FormTextField
              disabled
              label={t('Profile.GivenName.FirstName')}
              maxLength={
                (profile?.givenName?.firstName?.length ?? 0) > 25 ? 30 : 25
              }
              name="givenName.firstName"
              required
            />
            <FormTextField
              disabled
              label={t('Profile.GivenName.MiddleName')}
              maxLength={
                (profile?.givenName?.middleName?.length ?? 0) > 25 ? 30 : 25
              }
              name="givenName.middleName"
            />
            <FormTextField
              disabled
              label={t('Profile.GivenName.LastName')}
              maxLength={
                (profile?.givenName?.lastName?.length ?? 0) > 25 ? 50 : 25
              }
              name="givenName.lastName"
              required
            />
            <FormTextField
              disabled
              className={styles.suffix}
              label={t('Profile.GivenName.Suffix')}
              name="givenName.suffix"
              maxLength={10}
            />
            <FormTextField
              label={t('Profile.Address.Country')}
              name="country"
              required
              select
            >
              {countries?.map((country) => (
                <option key={country.abbreviation} value={country.abbreviation}>
                  {country.name}
                </option>
              ))}
            </FormTextField>
            <FormTextField
              label={t('Profile.Address.Address1')}
              maxLength={40}
              name="address1"
              required
            />
            <FormTextField
              label={t('Profile.Address.Address2')}
              maxLength={40}
              name="address2"
            />
            <FormTextField
              label={t('Profile.Address.Address3')}
              maxLength={40}
              name="address3"
            />
            <FormTextField
              label={t('Profile.Address.City')}
              maxLength={32}
              name="city"
              required
            />
            {states?.length > 0 && (
              <FormTextField
                label={t('Profile.Address.State')}
                name="state"
                required={['USA', 'CAN'].includes(
                  formik.values.country || 'USA',
                )}
                select
              >
                {states?.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name}
                  </option>
                ))}
              </FormTextField>
            )}
            <FormTextField
              label={t('Profile.Address.PostalCode')}
              maxLength={16}
              name="postalCode"
              required={['USA', 'CAN'].includes(formik.values.country || 'USA')}
            />
            <Button type={ButtonTypes.Submit}>
              {t('AddressVerificationModal.Button')}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </Modal>
  );
};
